// Time helpers that work in an explicit IANA timezone (the trip's, usually Asia/Tokyo),
// never the device's. All dates are 'YYYY-MM-DD' strings; all instants are epoch ms.

export interface ZonedParts {
  date: string // YYYY-MM-DD
  time: string // HH:MM
  weekday: number // 0 = Sunday
}

const fmtCache = new Map<string, Intl.DateTimeFormat>()
function formatter(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
    fmtCache.set(tz, f)
  }
  return f
}

function partsOf(ms: number, tz: string) {
  const p: Record<string, string> = {}
  for (const { type, value } of formatter(tz).formatToParts(ms)) p[type] = value
  return p as Record<'year' | 'month' | 'day' | 'hour' | 'minute' | 'second', string>
}

export function zoned(ms: number, tz: string): ZonedParts {
  const p = partsOf(ms, tz)
  const date = `${p.year}-${p.month}-${p.day}`
  return { date, time: `${p.hour}:${p.minute}`, weekday: new Date(`${date}T00:00:00Z`).getUTCDay() }
}

/** Offset of `tz` from UTC at instant `ms`, in minutes (Tokyo → +540). */
export function tzOffsetMinutes(ms: number, tz: string): number {
  const p = partsOf(ms, tz)
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second)
  return Math.round((asUtc - Math.floor(ms / 1000) * 1000) / 60000)
}

/** The instant at which the wall clock in `tz` reads `date` `time`. */
export function instantOf(date: string, time: string, tz: string): number {
  const guess = Date.parse(`${date}T${time}:00Z`)
  const first = guess - tzOffsetMinutes(guess, tz) * 60000
  // One correction pass covers DST edges; harmless where there is no DST.
  return guess - tzOffsetMinutes(first, tz) * 60000
}

export function addDays(date: string, n: number): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** Whole calendar days from `a` to `b` (b − a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000)
}

export function weekdayOf(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay()
}

export function dateRange(from: string, to: string): string[] {
  const out: string[] = []
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d)
  return out
}
